import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { Send, X, Loader2, User } from 'lucide-react';
import { discussionService } from '@/services/discussion.service';
import type { ForumDiscussionDto, DiscussionFormData } from '@/types/discussion.types';
import { extractMentions } from '@/types/discussion.types';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

interface DiscussionFormProps {
  threadId: string;
  parentId?: string | null;
  replyToUser?: { id: string; name: string };
  existingDiscussion?: ForumDiscussionDto;
  onSuccess: (discussion: ForumDiscussionDto) => void;
  onCancel?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export interface DiscussionFormRef {
  focus: () => void;
}

const DiscussionForm = forwardRef<DiscussionFormRef, DiscussionFormProps>(({
  threadId,
  parentId = null,
  replyToUser,
  existingDiscussion,
  onSuccess,
  onCancel,
  placeholder = 'Add a comment...',
  autoFocus = false,
}, ref) => {
  const [submitting, setSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isEditing = !!existingDiscussion;
  const { user } = useAuth();

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    },
  }));

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<DiscussionFormData>({
    defaultValues: {
      message: existingDiscussion?.message || '',
    },
  });

  const message = watch('message');

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  // Auto-focus if requested
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  const onSubmit = async (data: DiscussionFormData) => {
    try {
      setSubmitting(true);

      // Extract mentions from message
      const mentions = extractMentions(data.message);

      let result: ForumDiscussionDto;

      if (isEditing && existingDiscussion) {
        // Update existing discussion
        result = await discussionService.updateDiscussion({
          thread_id: threadId,
          discussion_id: existingDiscussion._id,
          message: data.message,
          mentioned_users: mentions,
        });
        toast.success('Comment updated!');
      } else {
        // Create new discussion
        result = await discussionService.createDiscussion({
          thread_id: threadId,
          message: data.message,
          parent_id: parentId,
          reply_to_user_id: replyToUser?.id,
          mentioned_users: mentions,
        });
        // toast.success('Comment posted!');
      }

      reset();
      setIsExpanded(false);
      setIsFocused(false);
      onSuccess(result);
    } catch (error: any) {
      console.error('Error submitting discussion:', error);
      toast.error(error?.message || `Failed to ${isEditing ? 'update' : 'post'} comment`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    setIsExpanded(false);
    setIsFocused(false);
    if (onCancel) {
      onCancel();
    } else {
      textareaRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setIsExpanded(true);
  };

  const handleBlur = () => {
    // Only collapse if there's no text
    if (!message?.trim() && !isEditing) {
      setTimeout(() => {
        setIsFocused(false);
        setIsExpanded(false);
      }, 150); // Delay to allow button clicks
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative">
      {/* Reply Context */}
      {replyToUser && !isEditing && (
        <div className="mb-3 text-xs text-gray-600 flex items-center gap-1">
          <span>Replying to</span>
          <span className="font-medium text-brand-indigo-600">@{replyToUser.name}</span>
        </div>
      )}

      {/* YouTube-style Comment Box */}
      <div className="flex gap-3">
        {/* User Avatar */}
        {!isEditing && (
          <div className="flex-shrink-0">
            {user?.profile_image ? (
              <img
                src={user.profile_image}
                alt={user.full_name || user.given_name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-indigo-500 to-brand-purple-600 flex items-center justify-center text-white font-medium text-sm">
                {user?.given_name?.[0]?.toUpperCase() || <User className="w-5 h-5" />}
              </div>
            )}
          </div>
        )}

        {/* Comment Input Area */}
        <div className="flex-1">
          {/* Textarea */}
          <div className="relative">
            <textarea
              {...register('message', {
                required: 'Comment cannot be empty',
                minLength: { value: 1, message: 'Comment too short' },
                maxLength: { value: 2000, message: 'Comment too long (max 2000 characters)' },
              })}
              ref={(e) => {
                register('message').ref(e);
                textareaRef.current = e;
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !submitting && message?.trim()) {
                  e.preventDefault();
                  handleSubmit(onSubmit)();
                }
              }}
              className={`w-full px-0 py-2 text-sm bg-transparent border-0 border-b-2 resize-none focus:outline-none transition-all duration-200 ${
                errors.message
                  ? 'border-red-500'
                  : isFocused
                    ? 'border-gray-900'
                    : 'border-gray-300'
              }`}
              placeholder={placeholder}
              rows={isExpanded ? 2 : 1}
              style={{
                minHeight: isExpanded ? '60px' : '40px',
                maxHeight: isExpanded ? '150px' : '40px',
                transition: 'min-height 0.2s ease, max-height 0.2s ease'
              }}
              aria-label="Add a comment to the discussion"
              aria-describedby={errors.message ? 'comment-error' : undefined}
              aria-invalid={!!errors.message}
            />
          </div>

          {/* Error Message */}
          {errors.message && (
            <p id="comment-error" role="alert" className="mt-2 text-xs text-red-500">
              {errors.message.message}
            </p>
          )}

          {/* Action Area - Only shown when expanded */}
          <div
            className={`transition-all duration-200 overflow-hidden ${
              isExpanded ? 'mt-3 opacity-100 max-h-20' : 'mt-0 opacity-0 max-h-0'
            }`}
          >
            {/* Character Count */}
            {!errors.message && message && message.length > 0 && (
              <div className="flex items-center justify-end mb-3">
                <p className={`text-xs font-medium transition-colors ${
                  message.length > 2000 ? 'text-red-500' :
                  message.length > 1800 ? 'text-orange-500' :
                  'text-gray-400'
                }`}>
                  {message.length} / 2000
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !message?.trim()}
                className="px-4 py-2 bg-brand-indigo-600 text-white text-sm font-medium rounded-full hover:bg-brand-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{isEditing ? 'Saving...' : 'Posting...'}</span>
                  </>
                ) : (
                  <span>{isEditing ? 'Save' : 'Comment'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
});

DiscussionForm.displayName = 'DiscussionForm';

export default DiscussionForm;
